import { Checkbox as CheckboxAria, composeRenderProps } from "react-aria-components";
import type { CheckboxProps } from "react-aria-components";
import React from "react";
import { tv } from "tailwind-variants";

import CheckmarkIcon from "@/assets/images/icons2/icon--checkmark-bold.svg?component";
import { cn } from "@/lib/helpers/cn";
import DashIcon from "@/assets/images/icons2/icon--dash.svg?component";

export function Checkbox2({ children, className, ...checkboxProps }: CheckboxProps) {
  return (
    <CheckboxAria className={cn(root(), className)} {...checkboxProps}>
      {composeRenderProps(children, (children, { isIndeterminate }) => (
        <>
          <figure className={box()}>
            {isIndeterminate ? (
              <DashIcon className={checkmark()} />
            ) : (
              <CheckmarkIcon className={checkmark()} />
            )}
          </figure>

          {children}
        </>
      ))}
    </CheckboxAria>
  );
}

const useCheckboxStyles = tv({
  slots: {
    box: cn(
      // Checkbox box styling
      "tw:flex tw:size-5 tw:shrink-0 tw:rounded",
      "tw:items-center tw:justify-center",
      "tw:transition-all tw:duration-200",

      // Unchecked state
      "tw:border tw:border-stroke-secondary tw:bg-fill-white",

      // Hover state (unchecked)
      "tw:group-hover:border-fill-teal-secondary",

      // Pressed state (unchecked)
      "tw:group-pressed:border-fill-teal-primary",

      // Focus state
      "tw:group-focus-visible:outline-2",
      "tw:group-focus-visible:outline-offset-2",
      "tw:group-focus-visible:outline-stroke-bright-purple-secondary",

      // Selected (checked) state
      "tw:group-selected:bg-fill-teal-primary",
      "tw:group-selected:border-fill-teal-primary",

      // Indeterminate state (same fill as selected)
      "tw:group-indeterminate:bg-fill-teal-primary",
      "tw:group-indeterminate:border-fill-teal-primary",

      // Hover state (checked)
      "tw:group-hover:group-selected:bg-fill-teal-secondary",
      "tw:group-hover:group-selected:border-fill-teal-secondary",

      // Pressed state (checked) - teal-primary lightened by 20% white
      "tw:group-pressed:group-selected:bg-[color-mix(in_srgb,var(--tw-color-fill-teal-primary),white_20%)]",
      "tw:group-pressed:group-selected:border-[color-mix(in_srgb,var(--tw-color-fill-teal-primary),white_20%)]",

      // Disabled state (unchecked)
      "tw:group-disabled:border-stroke-tertiary tw:group-disabled:bg-fill-quinary",

      // Disabled state (checked)
      "tw:group-disabled:group-selected:border-stroke-tertiary tw:group-disabled:group-selected:bg-fill-quaternary",
    ),
    checkmark: cn(
      "tw:size-3 tw:text-text-white",
      // Hide checkmark when not selected or indeterminate
      "tw:opacity-0 tw:group-indeterminate:opacity-100 tw:group-selected:opacity-100",
    ),
    root: cn(
      // Base layout
      "tw:group tw:mb-0! tw:flex! tw:cursor-pointer tw:items-center tw:gap-3 tw:font-body",

      // Label text
      "tw:text-text-primary",

      // Focus state - remove default outline since we apply it to the box
      "tw:outline-none",

      // Disabled state
      "tw:disabled:cursor-not-allowed",
    ),
  },
});

const { box, checkmark, root } = useCheckboxStyles();
